import React from "react";
import { Target, Gem, Infinity as InfinityIcon } from "lucide-react";
import { SectionHeader } from "@/sections/Services";

const PILLARS = [
  {
    icon: Target,
    title: "Clear scope",
    copy: "Projects are defined properly from the outset to avoid confusion, delays or unnecessary complexity.",
  },
  {
    icon: Gem,
    title: "Quality-first delivery",
    copy: "We prioritise clean builds, usability and long-term performance over quick shortcuts.",
  },
  {
    icon: InfinityIcon,
    title: "Long-term value",
    copy: "Our work is designed to support your business beyond launch — not just go live and stop.",
  },
];

export default function InvestmentApproach() {
  return (
    <section
      id="approach"
      data-testid="investment-approach-section"
      className="relative py-28 md:py-36 border-t border-white/[0.06]"
    >
      <div
        className="mk-orb"
        style={{ width: 460, height: 460, background: "#0A66FF", top: 80, left: -160, opacity: 0.2 }}
        aria-hidden
      />
      <div
        className="mk-orb"
        style={{ width: 380, height: 380, background: "#00D4FF", bottom: 60, right: -120, opacity: 0.18, animationDelay: "2s" }}
        aria-hidden
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-8">
        <SectionHeader
          eyebrow="Investment & approach"
          title="A focused, quality-led way of working"
        />

        <div className="mt-8 max-w-3xl space-y-5 text-[17px] text-slate-300 leading-relaxed">
          <p className="mk-reveal">
            Every project is different, but all work is approached with the same
            focus — delivering a high-quality, reliable and scalable solution.
          </p>
          <p className="mk-reveal" style={{ transitionDelay: "100ms" }}>
            We typically work with businesses that understand the value of doing
            things properly.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`approach-pillar-${i}`}
                className="mk-reveal mk-card rounded-2xl p-8 md:p-10 relative overflow-hidden"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="absolute -top-20 -right-20 h-48 w-48 rounded-full bg-gradient-to-br from-[#0A66FF]/15 to-transparent blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-[#0A66FF]/20 to-transparent">
                    <Icon className="h-5 w-5 text-[#00D4FF]" />
                  </div>
                  <h3 className="mt-7 font-display text-2xl md:text-[26px] tracking-tight text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[15px] text-slate-400 leading-relaxed">
                    {p.copy}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div
          className="mk-reveal mt-14 max-w-3xl rounded-2xl border border-white/10 bg-white/[0.03] px-7 py-6 md:px-9 md:py-7"
          data-testid="approach-closing-line"
        >
          <p className="text-[15.5px] md:text-base text-slate-300 leading-relaxed">
            <span className="text-white font-medium">We are best suited</span> to
            businesses looking for a reliable, well-executed solution rather than
            the lowest-cost option.
          </p>
        </div>
      </div>
    </section>
  );
}
